import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CLOCK_WINDOWS, mins, clock } from "@/lib/sleepData";

const GOLD = "#EBD9A6";
const R = 132;
const C = 160;

const pt = (m, r) => {
  const a = ((m / 1440) * 360 - 90) * (Math.PI / 180);
  return [C + r * Math.cos(a), C + r * Math.sin(a)];
};
const arc = (from, to, r) => {
  const [x1, y1] = pt(from, r);
  const [x2, y2] = pt(to, r);
  const large = ((to - from + 1440) % 1440) > 720 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
};

export default function ClockDial({ wake, onWake }) {
  const w = mins(wake || "07:00");
  const [on, setOn] = useState("light");
  const active = CLOCK_WINDOWS.find((x) => x.k === on);

  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(180deg, #151B3F, #0E1230)" }} data-testid="sleep-clock">
      <span className="absolute -left-32 top-10 w-[36vw] h-[36vw] rounded-full halo" style={{ background: "radial-gradient(circle, rgba(235,217,166,0.16), transparent 68%)", filter: "blur(40px)" }} />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <p className="text-[10px] tracking-[0.24em] uppercase text-white/40">The body clock</p>
          <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.02] mt-2 text-white">Move one hour and the whole day moves with it</h2>
          <p className="text-[14.5px] mt-4 max-w-md text-white/50">Every window below is measured from the hour you wake, not from the clock on the wall. Drag the wake hour and watch them all travel.</p>

          <div className="mt-7 flex items-center gap-4 flex-wrap">
            <div className="rounded-2xl px-5 py-3" style={{ background: "rgba(235,217,166,0.12)", border: "1px solid rgba(235,217,166,0.25)" }}>
              <p className="text-[10px] tracking-[0.16em] uppercase" style={{ color: GOLD }}>you wake at</p>
              <p className="font-display text-3xl tabular-nums text-white leading-none mt-1">{clock(w)}</p>
            </div>
            <input type="range" min={240} max={720} step={15} value={w} onChange={(e) => onWake(clock(Number(e.target.value)))}
              className="flex-1 min-w-[180px]" style={{ accentColor: GOLD }} aria-label="wake hour" data-testid="clock-wake-slider" />
          </div>

          <div className="mt-7 space-y-1.5">
            {CLOCK_WINDOWS.map((x) => (
              <button key={x.k} onClick={() => setOn(x.k)} className="w-full flex items-center gap-3.5 rounded-2xl px-4 py-3 text-left transition-all hover:translate-x-1"
                style={on === x.k ? { background: "rgba(255,255,255,0.09)", border: `1px solid ${x.tone}55` } : { background: "rgba(255,255,255,0.035)", border: "1px solid transparent" }}
                data-testid={`clock-window-${x.k}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: x.tone }} />
                <span className="font-display text-[16.5px] text-white/90 flex-1">{x.label}</span>
                <span className="text-[12px] tabular-nums shrink-0" style={{ color: on === x.k ? x.tone : "rgba(255,255,255,0.35)" }}>
                  {x.point ? clock(w + x.from) : `${clock(w + x.from)} – ${clock(w + x.to)}`}
                </span>
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.p key={on} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
              className="mt-5 text-[14px] leading-relaxed max-w-lg" style={{ color: "rgba(255,255,255,0.62)" }} data-testid="clock-window-say">{active?.say}</motion.p>
          </AnimatePresence>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2 flex justify-center">
          <svg viewBox="0 0 320 320" className="w-full max-w-[400px]">
            <circle cx={C} cy={C} r={R + 22} fill="rgba(255,255,255,0.02)" />
            <circle cx={C} cy={C} r={R} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
            {Array.from({ length: 24 }, (_, h) => {
              const [x1, y1] = pt(h * 60, R - (h % 6 === 0 ? 13 : 7));
              const [x2, y2] = pt(h * 60, R);
              return <line key={h} x1={x1} y1={y1} x2={x2} y2={y2} stroke={h % 6 === 0 ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.15)"} strokeWidth="1" />;
            })}
            {[0, 6, 12, 18].map((h) => {
              const [x, y] = pt(h * 60, R - 30);
              return <text key={h} x={x} y={y + 4} textAnchor="middle" fontSize="11" fill="rgba(255,255,255,0.35)" fontFamily="Outfit, sans-serif">{String(h).padStart(2, "0")}</text>;
            })}

            {CLOCK_WINDOWS.filter((x) => !x.point).map((x, i) => (
              <motion.path key={x.k} d={arc(w + x.from, w + x.to, R + 10 + i * 0)} fill="none" stroke={x.tone}
                strokeWidth={on === x.k ? 11 : 6} strokeLinecap="round" opacity={on === x.k ? 0.95 : 0.3}
                animate={{ d: arc(w + x.from, w + x.to, R + 10) }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => setOn(x.k)} style={{ cursor: "pointer" }} />
            ))}
            {CLOCK_WINDOWS.filter((x) => x.point).map((x) => {
              const [px, py] = pt(w + x.from, R + 10);
              return <motion.circle key={x.k} cx={px} cy={py} r={on === x.k ? 8 : 5.5} fill={x.tone} onClick={() => setOn(x.k)} style={{ cursor: "pointer" }} animate={{ cx: px, cy: py }} transition={{ duration: 0.5 }} />;
            })}

            {/* the sleep block */}
            <motion.path d={arc(w - 450, w, R - 44)} fill="none" stroke="#3A4292" strokeWidth="16" strokeLinecap="round" opacity="0.65"
              animate={{ d: arc(w - 450, w, R - 44) }} transition={{ duration: 0.5 }} />
            <text x={C} y={C - 8} textAnchor="middle" fontSize="13" fill="rgba(255,255,255,0.4)" fontFamily="Outfit, sans-serif" letterSpacing="2">AWAKE AT</text>
            <text x={C} y={C + 26} textAnchor="middle" fontSize="34" fill="#fff" fontFamily="Outfit, sans-serif">{clock(w)}</text>

            <motion.circle cx={pt(w, R)[0]} cy={pt(w, R)[1]} r="7" fill={GOLD} animate={{ cx: pt(w, R)[0], cy: pt(w, R)[1] }} transition={{ duration: 0.5 }} />
          </svg>
        </div>
      </div>
    </section>
  );
}
